/* Smolotov homepage behaviour. Loaded as a module by snippets/smolotov-home-assets.liquid, so it runs
   once however many sections render it. Every component initialises inside its own section root and
   re-initialises when the theme editor reloads a section. Nothing is hidden unless motion is allowed
   (html.smlh-motion), so no-JS and reduced-motion visitors always get the finished page. */

const MOTION = matchMedia('(prefers-reduced-motion: no-preference)').matches && 'IntersectionObserver' in window;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const money = cents => '$' + (cents / 100).toFixed(2);
if (MOTION) document.documentElement.classList.add('smlh-motion');

/* ─── cart: self-AJAX add (UpCart can't hijack it), then open the drawer ─── */
function openCartDrawer() {
  try {
    if (typeof window.upcartOpenCart === 'function') {
      const p = typeof window.upcartRefreshCart === 'function' ? window.upcartRefreshCart() : null;
      if (p && typeof p.then === 'function') p.then(() => window.upcartOpenCart()); else window.upcartOpenCart();
      return;
    }
  } catch (e) { /* fall through */ }
  const native = document.querySelector('cart-drawer-component, cart-drawer');
  if (native && typeof native.open === 'function') { native.open(); return; }
  window.location.href = '/cart';
}
async function addItems(items, button, status) {
  if (!items.length || button.disabled) return;
  const label = button.innerHTML;
  button.disabled = true; button.textContent = button.dataset.addingLabel || 'Adding…';
  if (status) status.textContent = '';
  try {
    const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
    const data = await res.json();
    if (!res.ok) throw data;
    openCartDrawer();
  } catch (err) {
    if (status) status.textContent = (err && (err.description || err.message)) || 'Could not add to cart. Please try again.';
  } finally {
    button.disabled = false; button.innerHTML = label;
  }
}

/* ─── reveal on scroll: one entrance language; groups stagger their children ─── */
let revealObserver;
function initReveals(root) {
  if (!MOTION) return;
  root.querySelectorAll('[data-smlh-reveal-group]').forEach(g => [...g.children].forEach((c, i) => {
    c.setAttribute('data-smlh-reveal', '');
    if (!c.style.getPropertyValue('--i')) c.style.setProperty('--i', Math.min(i, 8));
  }));
  revealObserver = revealObserver || new IntersectionObserver(entries => entries.forEach(e => {
    if (!e.isIntersecting) return;
    const el = e.target;
    revealObserver.unobserve(el);
    if (el.matches('.smlh-lineup, .smlh-vox-photos, .smlh-vox-texts')) [...el.children].forEach(c => c.classList.add('smlh-in'));   // swipe rows reveal as one unit
    el.classList.add('smlh-in');
    if (el.matches('.smlh-counter')) setTimeout(() => el.classList.add('smlh-settled'), 1400);
  }), { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  const rows = '.smlh-lineup, .smlh-vox-photos, .smlh-vox-texts';
  root.querySelectorAll('[data-smlh-reveal]').forEach(el => { if (!el.parentElement.matches(rows)) revealObserver.observe(el); });
  root.querySelectorAll(rows + ', .smlh-counter').forEach(el => revealObserver.observe(el));
}

/* ─── hero mugshots: one booking shot at a time, camera-flash cut ─── */
function initBooking(root) {
  root.querySelectorAll('.smlh-booking').forEach(booking => {
    const shots = [...booking.querySelectorAll('.smlh-shot')];
    if (shots.length < 2) return;
    const label = booking.querySelector('[data-shot-label]');
    const flash = booking.querySelector('.smlh-flash');
    const toggle = booking.querySelector('.smlh-shot-toggle');
    const every = (parseFloat(booking.dataset.interval) || 2.4) * 1000;
    let idx = 0, timer = null, paused = false, hovering = false, onScreen = true;
    // Face 1 is the LCP image; the others hold their URL in data-smlh-src until it has loaded.
    const fill = () => shots.forEach(s => {
      if (s.dataset.smlhSrcset) { s.srcset = s.dataset.smlhSrcset; delete s.dataset.smlhSrcset; }
      if (s.dataset.smlhSrc) { s.src = s.dataset.smlhSrc; delete s.dataset.smlhSrc; }
    });
    if (shots[0].complete) fill(); else ['load', 'error'].forEach(ev => shots[0].addEventListener(ev, fill, { once: true }));
    const ready = s => s.complete && s.naturalWidth > 0;
    const show = n => {
      if (!ready(shots[(n + shots.length) % shots.length])) return;
      if (MOTION) { flash.classList.remove('smlh-pop'); void flash.offsetWidth; flash.classList.add('smlh-pop'); }
      setTimeout(() => {
        shots[idx].classList.remove('smlh-is-on', 'smlh-just');
        idx = (n + shots.length) % shots.length;
        shots[idx].classList.add('smlh-is-on', 'smlh-just');
        if (label) label.textContent = shots[idx].dataset.label || '';
      }, MOTION ? 55 : 0);
    };
    const run = () => { clearInterval(timer); if (MOTION && !paused && !hovering && onScreen) timer = setInterval(() => show(idx + 1), every); };
    booking.addEventListener('click', e => { if (toggle && toggle.contains(e.target)) return; show(idx + 1); run(); });
    if (MOTION) {
      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => { paused = !paused; toggle.textContent = paused ? toggle.dataset.play : toggle.dataset.pause; run(); });
      }
      booking.addEventListener('mouseenter', () => { hovering = true; run(); });
      booking.addEventListener('mouseleave', () => { hovering = false; run(); });
      new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; run(); }).observe(booking);
      run();
    }
  });
}

/* ─── lineup: add a single carton ─── */
function initLineupCart(root) {
  root.querySelectorAll('[data-smlh-add]').forEach(btn => btn.addEventListener('click', () =>
    addItems([{ id: +btn.dataset.smlhAdd, quantity: 1 }], btn, btn.closest('.smlh-sheet')?.querySelector('.smlh-status'))));
}

/* ─── how-to: scroll-scrubbed reveal; rewinds on the way back up ─── */
function initHowto(root) {
  root.querySelectorAll('.smlh-howto[data-scrub]').forEach(howto => {
    if (!MOTION) return;
    const steps = [...howto.querySelectorAll('.smlh-step')];
    const grid = howto.querySelector('.smlh-howto-grid');
    const bar = howto.querySelector('.smlh-howto-progress');
    let ticking = false, live = false;
    howto.classList.add('smlh-scrub');
    const paint = () => {
      ticking = false;
      const vh = innerHeight, gTop = grid.getBoundingClientRect().top;
      const tops = steps.map(s => gTop + s.offsetTop);      // offsetTop ignores the reveal transform
      let sum = 0;
      steps.forEach((s, i) => {
        const col = tops.filter((t, j) => j < i && Math.abs(t - tops[i]) < 4).length;
        const t = clamp(((vh - tops[i]) / (vh * 0.62) - col * 0.14) / 0.62, 0, 1);
        const e = 1 - Math.pow(1 - t, 3);
        s.style.setProperty('--rv', e.toFixed(3)); sum += e;
      });
      const p = sum / steps.length;
      if (bar) bar.style.setProperty('--p', p.toFixed(3));
      howto.classList.toggle('smlh-is-done', p > 0.985);
    };
    const onScroll = () => { if (live && !ticking) { ticking = true; requestAnimationFrame(paint); } };
    new IntersectionObserver(([e]) => { live = e.isIntersecting; if (live) paint(); }, { rootMargin: '300px 0px' }).observe(howto);
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll);
    paint();
  });
}

/* ─── bundle builder: Shopify-exact maths from product price + discount % + shipping bands ─── */
function initBuilder(root) {
  root.querySelectorAll('.smlh-builder[data-config]').forEach(builder => {
    const cfg = JSON.parse(builder.dataset.config);
    const chars = cfg.products;                               // [{id, title, color, img, price(cents)}]
    if (!chars.length) return;
    const $slots = builder.querySelector('.smlh-slots');
    const $cartons = builder.querySelector('.smlh-cartons');
    const $add = builder.querySelector('.smlh-add-slot');
    const $status = builder.querySelector('.smlh-builder-status');
    const $totals = builder.querySelector('.smlh-totals');
    const $nudge = builder.querySelector('.smlh-nudge');
    const $cta = builder.querySelector('.smlh-builder-cta');
    const $count = builder.querySelector('.smlh-b-status');
    const pct = { 2: cfg.pct2, 3: cfg.pct3 };
    const max = Math.min(3, Math.max(1, cfg.max || 3));
    let slots = chars.slice(0, 3).map((_, i) => i);
    while (slots.length < 3) slots.push(0);

    const priceFor = list => {                                 // per-unit % floored to the cent, like Shopify
      const n = list.length, p = pct[n] || 0;
      return list.reduce((sum, i) => sum + chars[i].price - Math.floor(chars[i].price * p / 100), 0);
    };
    const shipping = c => c >= cfg.freeAt ? 0 : (c >= cfg.band1 ? cfg.rate2 : cfg.rate1);

    $cartons.innerHTML = [0, 1, 2].map(i => `<div class="smlh-carton" style="--ci:${i}"><span class="smlh-carton-name"></span>
      <div class="smlh-ticks" aria-hidden="true">${Array.from({ length: 60 }, (_, k) => `<i class="smlh-tick" style="--t:${k}"></i>`).join('')}</div></div>`).join('');
    const cartonEls = [...$cartons.children];

    const renderSlots = () => {
      $slots.innerHTML = slots.map((key, i) => `
        <li class="smlh-slot">
          <span class="smlh-slot-label">${cfg.cartonLabel} ${i + 1}</span>
          <div class="smlh-chips" role="radiogroup" aria-label="${cfg.cartonLabel} ${i + 1}">
            ${chars.map((c, k) => `<label class="smlh-chip"><input type="radio" name="${builder.id}-slot${i}" value="${k}" ${k === key ? 'checked' : ''} data-slot="${i}">
              <span><img src="${c.img}" alt="" width="30" height="30" loading="lazy">${c.title}</span></label>`).join('')}
          </div>
          <button class="smlh-remove" type="button" data-remove="${i}" ${slots.length === 1 ? 'disabled' : ''}>${cfg.removeLabel}</button>
        </li>`).join('');
      $add.hidden = slots.length >= max;
    };
    const renderCounter = () => cartonEls.forEach((el, i) => {
      const k = slots[i], on = k !== undefined;
      el.classList.toggle('smlh-off', !on);
      el.style.setProperty('--c', on ? chars[k].color : 'transparent');
      el.querySelector('.smlh-carton-name').textContent = on ? chars[k].title : cfg.emptyLabel;
    });
    const tiers = n => {                                       // Shopify-exact numbers for n cartons of the current mix
      const list = slots.slice(0, n).concat(Array(Math.max(0, n - slots.length)).fill(slots[slots.length - 1]));
      const gross = list.reduce((s, i) => s + chars[i].price, 0), price = priceFor(list), ship = shipping(price);
      return { gross, price, ship, delivered: price + ship, perbag: Math.round((price + ship) / (n * 60)) };
    };
    const fill = (tpl, vals) => tpl.replace(/\{(\w+)\}/g, (m, k) => (k in vals ? vals[k] : m));
    const renderTotals = () => {
      const n = slots.length, t = tiers(n), t1 = tiers(1), t2 = tiers(2), t3 = tiers(Math.min(3, max));
      const next = n < max ? tiers(n + 1) : t;
      const vals = {
        n, max, bags: n * 60, unit: money(chars[slots[0]].price), price: money(t.price),
        save2: money(t2.gross - t2.price), save3: money(t3.gross - t3.price), save: money(t.gross - t.price),
        extra: money(next.delivered - t.delivered), ship: t.ship ? money(t.ship) : cfg.freeLabel.toLowerCase(),
        ship2: money(t2.ship), perbag: t.perbag + '¢', perbag1: t1.perbag + '¢', perbag3: t3.perbag + '¢'
      };
      $totals.innerHTML = `
        <div><dt>${fill(cfg.words[n - 1], vals)}</dt><dd>${money(t.gross)}</dd></div>
        ${t.gross > t.price ? `<div><dt>${cfg.discountLabel}</dt><dd class="smlh-disc">−${money(t.gross - t.price)}</dd></div>` : ''}
        <div><dt>${cfg.shippingLabel}</dt><dd class="${t.ship ? '' : 'smlh-free'}">${t.ship ? money(t.ship) : cfg.freeLabel}</dd></div>
        <div class="smlh-grand"><dt>${cfg.deliveredLabel}</dt><dd>${money(t.delivered)}</dd></div>`;
      $count.textContent = fill(cfg.statusTpl, vals);
      $cta.innerHTML = fill(n === 1 ? cfg.ctaOne : cfg.ctaMany, vals);
      $nudge.textContent = fill(cfg.nudges[n - 1] || '', vals);
      $nudge.classList.remove('smlh-pop'); void $nudge.offsetWidth; $nudge.classList.add('smlh-pop');
    };
    const render = () => { renderSlots(); renderCounter(); renderTotals(); };

    $slots.addEventListener('change', e => { if (e.target.matches('input[type=radio]')) { slots[+e.target.dataset.slot] = +e.target.value; renderCounter(); } });
    $slots.addEventListener('click', e => { const b = e.target.closest('[data-remove]'); if (b && slots.length > 1) { slots.splice(+b.dataset.remove, 1); render(); } });
    $add.addEventListener('click', () => { const used = new Set(slots); slots.push(chars.findIndex((_, k) => !used.has(k)) >= 0 ? chars.findIndex((_, k) => !used.has(k)) : 0); render(); });
    $cta.addEventListener('click', () => {
      const q = {}; slots.forEach(i => { q[chars[i].id] = (q[chars[i].id] || 0) + 1; });
      addItems(Object.entries(q).map(([id, quantity]) => ({ id: +id, quantity })), $cta, $status);
    });
    render();
  });
}

/* ─── sticky bar: mobile only; shows when past the hero and no other primary CTA, builder or footer is in view ─── */
function initSticky() {
  const sticky = document.querySelector('.smlh-sticky');
  if (!sticky || sticky.dataset.ready) return;
  sticky.dataset.ready = '1';
  const hero = document.querySelector('.smlh-hero');
  const mobile = matchMedia('(max-width: 899px)');
  let pastHero = !hero; const loud = new Set();
  const sync = () => sticky.classList.toggle('smlh-show', mobile.matches && pastHero && loud.size === 0);
  if (hero) new IntersectionObserver(([e]) => { pastHero = !e.isIntersecting && e.boundingClientRect.top < 0; sync(); }).observe(hero);
  const watch = new IntersectionObserver(es => { es.forEach(e => e.isIntersecting ? loud.add(e.target) : loud.delete(e.target)); sync(); });
  document.querySelectorAll('.smlh-root .smlh-btn-primary, .smlh-box, footer, [id$="__footer"]').forEach(el => { if (!sticky.contains(el)) watch.observe(el); });
  mobile.addEventListener('change', sync);
}

/* ─── four bags: scroll-linked rise out of the footer edge; the asterisk turns ─── */
function initBags(root) {
  root.querySelectorAll('.smlh-bags[data-animate]').forEach(bagsEl => {
    if (!MOTION) return;
    const bags = [...bagsEl.querySelectorAll('.smlh-bag')];
    const ast = bagsEl.closest('.smlh-thanks')?.querySelector('.smlh-ast');
    let ticking = false, live = false;
    const paint = () => {
      ticking = false;
      const r = bagsEl.getBoundingClientRect();
      const p = clamp((innerHeight - r.top) / (r.height + innerHeight * 0.25), 0, 1);
      bags.forEach((b, i) => {
        const e = 1 - Math.pow(1 - clamp((p - i * 0.12) / 0.55, 0, 1), 3);
        b.style.transform = `translateY(${((1 - e) * 88).toFixed(2)}%) rotate(${((1 - e) * (i % 2 ? 5 : -5)).toFixed(2)}deg)`;
      });
      if (ast) ast.style.setProperty('--spin', (p * 360).toFixed(1) + 'deg');
    };
    const onScroll = () => { if (live && !ticking) { ticking = true; requestAnimationFrame(paint); } };
    new IntersectionObserver(([e]) => { live = e.isIntersecting; if (live) paint(); }, { rootMargin: '200px 0px' }).observe(bagsEl);
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll);
    paint();
  });
}

/* ─── headlines: never more than two lines. Counts real text lines and steps the size down until it fits,
   so any copy typed in the theme editor still lands on two lines. ─── */
function textLines(el) {
  const tol = parseFloat(getComputedStyle(el).fontSize) * 0.5, tops = [], range = document.createRange();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!n.textContent.trim()) continue;
    range.selectNodeContents(n);
    for (const r of range.getClientRects()) if (r.width > 1 && !tops.some(t => Math.abs(t - r.top) < tol)) tops.push(r.top);
  }
  return tops.length;
}
function fitHeadlines(root) {
  root.querySelectorAll('.smlh-root h2').forEach(h => {
    h.style.fontSize = '';
    let fs = parseFloat(getComputedStyle(h).fontSize);
    const min = Math.max(20, fs * 0.4);
    for (let i = 0; i < 40 && fs > min && textLines(h) > 2; i++) { fs *= 0.95; h.style.fontSize = fs.toFixed(1) + 'px'; }
  });
}
let fitTimer;
addEventListener('resize', () => { clearTimeout(fitTimer); fitTimer = setTimeout(() => fitHeadlines(document), 150); });

/* ─── read more: only on quotes that are actually clamped ─── */
function initClamps(root) {
  root.querySelectorAll('.smlh-vox').forEach(sec => {
    const more = sec.dataset.readMore || 'Read more', less = sec.dataset.readLess || 'Show less';
    sec.querySelectorAll('.smlh-clamp').forEach(q => {
      if (q.dataset.clampReady || q.scrollHeight <= q.clientHeight + 2) return;
      q.dataset.clampReady = '1';
      const btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'smlh-more'; btn.textContent = more; btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', () => { const open = q.classList.toggle('smlh-open'); btn.textContent = open ? less : more; btn.setAttribute('aria-expanded', String(open)); });
      q.after(btn);
    });
  });
}

function init(root = document) {
  fitHeadlines(root); initReveals(root); initClamps(root); initBooking(root); initLineupCart(root); initHowto(root); initBuilder(root); initBags(root); initSticky();
}
init();
if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { fitHeadlines(document); initClamps(document); });
document.addEventListener('shopify:section:load', e => init(e.target));
